import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageCircle, MessagesSquare, Users, Sparkles, ChevronLeft,
  Flame, Eye, Heart, ArrowLeft,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { t } from "@/lib/i18n";
import { timeAgo } from "@/lib/format";

export default function Home() {
  const [data, setData] = useState(null);
  const [trending, setTrending] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    Promise.all([
      api.get("/index"),
      api.get("/trending?limit=6"),
    ])
      .then(([a, b]) => {
        setData(a.data);
        setTrending(b.data.threads);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-10" data-testid="home-page">
      {/* Hero */}
      {!user && (
        <section className="relative overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-bl from-primary via-primary to-primary/95 text-primary-foreground p-8 sm:p-12 motif-header">
          <div className="relative z-10 max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-3 py-1 text-xs font-eng mb-4">
              <Sparkles size={12} /> <span>New — 2026</span>
            </div>
            <h1 className="urdu-naskh text-4xl sm:text-5xl mb-3 font-bold">{t.welcome}</h1>
            <p className="urdu-naskh text-lg sm:text-xl opacity-95 mb-6">{t.heroSub}</p>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold">
                <Link to="/register" data-testid="hero-register">{t.joinNow}</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white/40 bg-transparent text-primary-foreground hover:bg-white/10 hover:text-primary-foreground">
                <a href="#forums" data-testid="hero-browse">{t.browseForums}</a>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Stats strip */}
      {data && (
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 font-eng">
          <StatBox icon={<MessagesSquare size={18} />} label={t.totalThreads} value={data.stats.threads} />
          <StatBox icon={<MessageCircle size={18} />} label={t.totalPosts} value={data.stats.posts} />
          <StatBox icon={<Users size={18} />} label={t.totalMembers} value={data.stats.users} />
          <StatBox
            icon={<Sparkles size={18} />}
            label={<span className="urdu-naskh">{t.newest}</span>}
            value={
              data.stats.newest_user ? (
                <Link className="hover:underline" to={`/u/${data.stats.newest_user.username}`}>
                  {data.stats.newest_user.username}
                </Link>
              ) : "—"
            }
          />
        </section>
      )}

      {/* Trending */}
      {trending && trending.length > 0 && (
        <section data-testid="trending-section">
          <div className="flex items-baseline justify-between border-b-2 border-accent/70 pb-2 mb-3">
            <h2 className="urdu-naskh text-2xl text-primary flex items-center gap-2">
              <Flame size={20} className="text-accent" />
              {t.trending}
            </h2>
            <div className="urdu-naskh text-xs text-muted-foreground hidden sm:block">{t.trendingSub}</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {trending.map((th, i) => (
              <Link
                key={th.id}
                to={`/t/${th.id}`}
                className="trending-card block bg-card border border-border rounded-xl p-4 group"
                data-testid={`trending-${th.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 shrink-0 rounded-lg bg-primary/10 border border-primary/20 text-primary flex items-center justify-center font-eng font-bold text-sm">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="urdu-naskh text-base leading-snug text-foreground group-hover:text-primary line-clamp-2">
                      {th.title}
                    </h3>
                    <div className="mt-2 flex items-center gap-3 text-[11px] text-muted-foreground font-eng">
                      <span className="urdu-naskh text-primary/80">{th.forum_name}</span>
                    </div>
                    <div className="mt-1.5 flex items-center gap-3 text-[11px] text-muted-foreground font-eng">
                      <span className="flex items-center gap-1"><MessageCircle size={11} /> {th.reply_count}</span>
                      <span className="flex items-center gap-1"><Eye size={11} /> {th.view_count}</span>
                      <span className="flex items-center gap-1"><Heart size={11} /> {th.like_total || 0}</span>
                      <span className="me-auto opacity-70">{timeAgo(th.last_post_at)}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Forum listing */}
      <section id="forums" className="space-y-8">
        {loading && (
          <div className="space-y-3">
            <Skeleton className="h-8 w-40" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}
        {data?.categories?.map((cat) => (
          <div key={cat.id} data-testid={`category-${cat.id}`}>
            <div className="flex items-baseline justify-between border-b-2 border-primary/80 pb-2 mb-3">
              <h2 className="urdu-naskh text-2xl text-primary font-semibold">{cat.name}</h2>
              <div className="urdu-naskh text-xs text-muted-foreground hidden sm:block">{cat.description}</div>
            </div>
            <Card className="divide-y divide-border overflow-hidden">
              {(data.forums_by_category[cat.id] || []).map((f) => (
                <ForumRow key={f.id} forum={f} />
              ))}
              {(data.forums_by_category[cat.id] || []).length === 0 && (
                <div className="px-4 py-6 text-center urdu-naskh text-muted-foreground">{t.none}</div>
              )}
            </Card>
          </div>
        ))}
      </section>
    </div>
  );
}

function ForumRow({ forum: f }) {
  return (
    <div className="forum-row bg-card group" data-testid={`forum-row-${f.id}`}>
      <div className="flex items-start gap-4 px-4 sm:px-5 py-4">
        <div className="flex-1 min-w-0">
          <Link to={`/f/${f.id}`} className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0">
              <MessagesSquare size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <h3 className="urdu-naskh text-lg font-semibold text-primary group-hover:underline underline-offset-4">
                  {f.name}
                </h3>
                <ChevronLeft size={14} className="link-arrow text-primary/70" />
              </div>
              <p className="urdu-naskh text-sm text-muted-foreground mt-0.5">{f.description}</p>
            </div>
          </Link>
          {f.subforums?.length > 0 && (
            <div className="mt-2 mr-14 flex flex-wrap gap-x-3 gap-y-1 text-xs" data-testid={`sub-list-${f.id}`}>
              <span className="urdu-naskh text-muted-foreground">{t.subForums}:</span>
              {f.subforums.map((sf) => (
                <Link
                  key={sf.id}
                  to={`/f/${sf.id}`}
                  className="urdu-naskh text-primary/85 hover:text-primary hover:underline"
                  data-testid={`sub-link-${sf.id}`}
                >
                  {sf.name}
                </Link>
              ))}
            </div>
          )}
        </div>
        <div className="hidden md:flex flex-col items-end text-xs text-muted-foreground font-eng gap-0.5 shrink-0">
          <div><span className="font-semibold text-foreground">{f.thread_count_total ?? f.thread_count}</span> {t.threads}</div>
          <div><span className="font-semibold text-foreground">{f.post_count_total ?? f.post_count}</span> {t.posts}</div>
        </div>
        <div className="hidden lg:block w-56 shrink-0 border-r border-border pr-4 text-xs">
          {f.last_post ? (
            <div className="space-y-0.5">
              <Link to={`/t/${f.last_post.thread_id}`} className="urdu-naskh text-primary hover:underline line-clamp-1">
                {f.last_post.title}
              </Link>
              <div className="text-muted-foreground font-eng">
                {t.by} <span className="text-foreground">{f.last_post.username}</span>
              </div>
              <div className="text-muted-foreground font-eng opacity-70">
                {timeAgo(f.last_post.at)}
              </div>
            </div>
          ) : (
            <div className="urdu-naskh text-muted-foreground">— {t.none}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatBox({ icon, label, value }) {
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3 flex items-center gap-3">
      <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center">{icon}</div>
      <div>
        <div className="text-xs text-muted-foreground urdu-naskh">{label}</div>
        <div className="font-eng font-semibold text-lg text-foreground">{value}</div>
      </div>
    </div>
  );
}
